import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ArrowLeft, Check, ChevronDown, ChevronRight, Image as ImageIcon, MapPin, PackageCheck, PackagePlus, ShoppingBag, Truck, Warehouse, X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useXomicsAuth } from './AuthContext';
import {
  cancelStockRequest, createStockRequest, loadLocationProductStock, loadOfficeStock, loadOfficeStockRequests, loadProducts, loadRequestWarehouses,
  loadStockLocations, loadStockRequest, loadWarehouseStockRequests, receiveStockRequest,
} from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs, XomicsSelect, useLeave,
} from './parts';
import ShippingComposer from './ShippingComposer';
import { ShipmentLabelButtons } from './ShippingLabelButton';
import { OfficeNamed, StoreOfficeLine, officeTitle, useStoreOffice } from './StoreOffice';
import { DispatchStock, EntryBoxes, countOf, isLeft } from './StoreStock';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './productDetail.css';
import './products.css';
import './storeOffice.css';
import './stockRequests.css';

// Stock between an office and a warehouse.
//
// Selling, Stock requests is the office's side: what it asked the warehouses
// for, and taking it in when it arrives. Warehouse, Requests is the
// warehouse's: what the offices asked it for, the label for each, and marking
// the parcel dispatched, which takes it off the warehouse's stock. A request
// opens as a sheet that shows where it stands and the one thing to do next,
// for whichever side is looking. A new request is two steps: what is needed,
// then from which warehouse (the nearest first, with what each has).

const STATUS = Object.freeze({
  pending: { label: 'ui.stock_requests.status.pending', tone: 'info' },
  label: { label: 'ui.stock_requests.status.label', tone: 'warn' },
  dispatched: { label: 'ui.stock_requests.status.dispatched', tone: 'info' },
  received: { label: 'ui.stock_requests.status.received', tone: 'ok' },
  cancelled: { label: 'ui.stock_requests.status.cancelled', tone: 'flat' },
});
// What each side has open, and how the rest is grouped.
const TABS = Object.freeze({
  office: [
    { value: 'open', label: 'ui.stock_requests.tab.open', statuses: ['pending', 'label', 'dispatched'] },
    { value: 'received', label: 'ui.stock_requests.status.received', statuses: ['received'] },
    { value: 'cancelled', label: 'ui.stock_requests.status.cancelled', statuses: ['cancelled'] },
  ],
  warehouse: [
    { value: 'new', label: 'ui.stock_requests.tab.new', statuses: ['pending'] },
    { value: 'label', label: 'ui.stock_requests.tab.to_dispatch', statuses: ['label'] },
    { value: 'sent', label: 'ui.stock_requests.tab.sent', statuses: ['dispatched', 'received'] },
    { value: 'cancelled', label: 'ui.stock_requests.status.cancelled', statuses: ['cancelled'] },
  ],
});

const when = (at) => new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const day = (at) => new Date(at).toLocaleDateString(undefined, { dateStyle: 'medium' });
const numberOf = (request) => tx('ui.stock_requests.number', { number: request.number });
const lineName = (line) => (line.label ? `${line.name} · ${line.label}` : line.name);
// Only what is shipped is asked for: a service or a subscription does not run out.
const keepsStock = (product) => product.shippingWeightGrams !== null;

function placeAddress(place) {
  const { line1, line2, postalCode, city, region } = place.address;
  return [line1, line2, postalCode, city, region].filter((part) => typeof part === 'string' && part.trim() !== '').join(', ');
}

function StatusPill({ status }) {
  const shown = STATUS[status] ?? STATUS.pending;
  return <Pill tone={shown.tone}>{tx(shown.label)}</Pill>;
}

function RequestList({ requests, side, onOpen }) {
  if (requests.length === 0) return <Empty title={tx('ui.stock_requests.none')} />;
  return (
    <div className="xo-pc-list is-requests">
      <div className="xo-pc-row is-head" aria-hidden="true">
        <span>{tx('ui.stock_requests.request')}</span>
        <span>{tx('ui.stock_requests.items')}</span>
        <span>{tx('ui.stock_requests.asked_on')}</span>
        <span>{tx('ui.store.status')}</span>
        <span />
      </div>
      {requests.map((request) => {
        const other = side === 'office' ? request.warehouse : request.office;
        return (
          <button type="button" key={request.uuid} className="xo-pc-row" onClick={() => onOpen(request.uuid)}>
            <span className="xo-pc-name">
              <span className="xo-pc-thumb is-icon">{side === 'office' ? <Warehouse aria-hidden="true" /> : <PackagePlus aria-hidden="true" />}</span>
              <span><strong>{numberOf(request)}</strong><small>{other.name} · {other.address.city}</small></span>
            </span>
            <span className="xo-pc-number">{request.itemCount.toLocaleString()}</span>
            <span className="xo-request-when">{day(request.createdAt)}</span>
            <span><StatusPill status={request.status} /></span>
            <ChevronRight aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

// Where a request stands: asked, label made, on its way, received. A request
// called off stops where it was.
function RequestSteps({ request }) {
  const steps = [
    { key: 'asked', label: tx('ui.stock_requests.step.asked'), at: request.createdAt, by: request.createdBy, done: true },
    { key: 'label', label: tx('ui.stock_requests.step.label'), at: null, by: null, done: request.shipment !== null },
    {
      key: 'dispatched', label: tx('ui.stock_requests.step.dispatched'), at: request.shipment?.dispatchedAt ?? null, by: null,
      done: ['dispatched', 'received'].includes(request.status),
    },
    { key: 'received', label: tx('ui.stock_requests.step.received'), at: request.receivedAt, by: request.receivedBy, done: request.status === 'received' },
  ];
  return (
    <ol className="xo-request-steps">
      {steps.map((step) => (
        <li key={step.key} className={step.done ? 'is-done' : undefined}>
          <i aria-hidden="true">{step.done && <Check />}</i>
          <span>
            <strong>{step.label}</strong>
            {step.done && (step.at || step.by) && <small>{[step.at ? when(step.at) : null, step.by].filter(Boolean).join(' · ')}</small>}
            {step.key === 'label' && request.shipment?.trackingNumber && (
              <small>{[request.shipment.carrierName, request.shipment.trackingNumber].filter(Boolean).join(' · ')}</small>
            )}
          </span>
        </li>
      ))}
      {request.status === 'cancelled' && (
        <li className="is-stopped">
          <i aria-hidden="true"><X /></i>
          <span>
            <strong>{tx('ui.stock_requests.status.cancelled')}</strong>
            <small>{[when(request.cancelledAt), request.cancelledBy].filter(Boolean).join(' · ')}</small>
            {request.cancelNote && <em dir="auto">{request.cancelNote}</em>}
          </span>
        </li>
      )}
    </ol>
  );
}

// A small window that asks before something cannot be taken back: calling a
// request off (with a note), or taking its stock in.
function Confirm({
  title, detail, lines = null, withNote = false, action, busyLabel, onClose, onConfirm,
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const close = useLeave(busy, note.trim() !== '', onClose);
  async function confirm(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onConfirm(note);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }
  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className="xo-pc-form is-narrow" role="dialog" aria-modal="true" aria-labelledby="xo-request-confirm-title"
        onSubmit={confirm} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon"><PackageCheck aria-hidden="true" /></span>
          <div><h2 id="xo-request-confirm-title">{title}</h2><p>{detail}</p></div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>
        {(lines || withNote) && (
          <div className="xo-pc-form-body">
            {lines && (
              <ul className="xo-stock-takes">
                {lines.map((line) => (
                  <li key={`${line.productId}-${line.sku ?? ''}`}><b>{line.quantity.toLocaleString()} ×</b><span>{lineName(line)}</span></li>
                ))}
              </ul>
            )}
            {withNote && (
              <label className="xo-stock-field is-wide">
                <span>{tx('ui.store.stock.note')}</span>
                <input type="text" maxLength={500} value={note} disabled={busy} dir="auto" onChange={(event) => setNote(event.target.value)} />
              </label>
            )}
          </div>
        )}
        <footer className="xo-pc-form-foot">
          {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>{tx('ui.common.cancel')}</button>
          <button type="submit" className="xo-btn" disabled={busy}>{busy ? busyLabel : action}</button>
        </footer>
      </form>
    </div>
  );
}

// One request as a sheet: what is asked for, where it stands, and what this
// side can do with it next.
function RequestSheet({ uuid, pictures, onClose, onChanged }) {
  const { permissions } = useXomicsAuth();
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [asking, setAsking] = useState(null);
  const read = useCallback(async () => {
    try {
      setRequest(await loadStockRequest(uuid));
      setError('');
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, [uuid]);
  useEffect(() => { read(); }, [read]);
  const close = useLeave(asking !== null, false, onClose);
  const changed = async () => { await read(); onChanged(); };

  const sides = request?.sides ?? { office: false, warehouse: false };
  const mayShip = permissions.includes('SHIPMENT_MANAGE');

  return (<>
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="xo-pm xo-request-sheet" role="dialog" aria-modal="true" aria-labelledby="xo-request-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pm-head">
          <span className="xo-pm-thumb is-icon"><PackagePlus aria-hidden="true" /></span>
          <div className="xo-pm-title">
            <h2 id="xo-request-title">{request ? numberOf(request) : tx('ui.store.stock.reading')}</h2>
            {request && <p>{tx('ui.stock_requests.from_to', { from: request.warehouse.name, to: officeTitle(request.office) })}</p>}
          </div>
          <div className="xo-pm-head-actions">
            {request && <StatusPill status={request.status} />}
            <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} onClick={close}>
              <X aria-hidden="true" /></button>
          </div>
        </header>

        <div className="xo-pm-body">
          {request === null ? (error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <Loading />) : (
            <div className="xo-request-layout">
              <div className="xo-request-main">
                <section className="xo-pm-card">
                  <h3>{tx('ui.stock_requests.asked_for')}</h3>
                  <ul className="xo-request-lines">
                    {request.lines.map((line) => {
                      const picture = pictures.get(line.productId) ?? null;
                      return (
                        <li key={`${line.productId}-${line.sku ?? ''}`}>
                          <span className="xo-pc-thumb">{picture ? <img src={picture} alt="" /> : <ImageIcon aria-hidden="true" />}</span>
                          <span><strong>{line.name}</strong>{line.label && <small>{line.label}</small>}</span>
                          <b>{line.quantity.toLocaleString()}</b>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="xo-request-total">{tx('ui.store.stock.in_all', { count: request.itemCount.toLocaleString() })}</p>
                </section>
                {request.note && (
                  <section className="xo-pm-card">
                    <h3>{tx('ui.stock_requests.note')}</h3>
                    <p className="xo-request-note" dir="auto">{request.note}</p>
                  </section>
                )}

                {/* The warehouse: its label, then marking the parcel dispatched. */}
                {sides.warehouse && request.status === 'pending' && mayShip && (
                  <section className="xo-pm-card xo-request-label">
                    <ShippingComposer subject={{ subjectType: 'stock_request', subjectUuid: request.uuid }} onDone={changed} />
                  </section>
                )}
                {sides.warehouse && request.status === 'label' && (
                  <section className="xo-pm-card xo-request-next">
                    <h3>{tx('ui.stock_requests.parcel')}</h3>
                    <div className="xo-request-downloads"><ShipmentLabelButtons labels={request.shipment.labels ?? []} className="xo-btn-2" /></div>
                    {mayShip && (
                      <button type="button" className="xo-btn" onClick={() => setAsking('dispatch')}>
                        <Truck aria-hidden="true" />{tx('ui.carrier.mark_dispatched')}
                      </button>
                    )}
                  </section>
                )}
                {/* The office: taking it in once it is on its way. */}
                {sides.office && request.status === 'dispatched' && (
                  <section className="xo-pm-card xo-request-next">
                    <h3>{tx('ui.stock_requests.arrived_title')}</h3>
                    <p className="xo-muted">{tx('ui.stock_requests.arrived_hint')}</p>
                    <button type="button" className="xo-btn" onClick={() => setAsking('receive')}>
                      <PackageCheck aria-hidden="true" />{tx('ui.stock_requests.mark_received')}
                    </button>
                  </section>
                )}
                {sides.office && request.status === 'label' && (
                  <p className="xo-muted xo-request-wait">{tx('ui.stock_requests.waiting_dispatch')}</p>
                )}
                {sides.office && request.status === 'pending' && (
                  <p className="xo-muted xo-request-wait">{tx('ui.stock_requests.waiting_label')}</p>
                )}
                {request.status === 'pending' && (sides.office || sides.warehouse) && (
                  <button type="button" className="xo-link xo-request-cancel" onClick={() => setAsking('cancel')}>
                    {tx('ui.stock_requests.cancel')}
                  </button>
                )}
              </div>

              <aside className="xo-request-side">
                <section className="xo-pm-card">
                  <h3>{tx('ui.stock_requests.progress')}</h3>
                  <RequestSteps request={request} />
                </section>
                <section className="xo-pm-card xo-request-places">
                  <h3>{tx('ui.stock_requests.route')}</h3>
                  <div><Warehouse aria-hidden="true" /><span><strong>{request.warehouse.name}</strong><small>{placeAddress(request.warehouse)}</small></span></div>
                  <div><MapPin aria-hidden="true" /><span><strong>{officeTitle(request.office)}</strong><small>{placeAddress(request.office)}</small></span></div>
                  {request.distanceKm !== null && <p className="xo-muted">{tx('ui.stock_requests.distance', { km: request.distanceKm.toLocaleString() })}</p>}
                </section>
              </aside>
            </div>
          )}
        </div>

        <footer className="xo-pc-form-foot">
          {error && request !== null ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" onClick={close}>{tx("ui.products.close_bbfa773e")}</button>
        </footer>
      </section>
    </div>
    {asking === 'dispatch' && request?.shipment && (
      <DispatchStock shipmentUuid={request.shipment.uuid} onClose={() => setAsking(null)}
        onDispatched={() => { setAsking(null); changed(); }} />
    )}
    {asking === 'receive' && (
      <Confirm title={tx('ui.stock_requests.mark_received')}
        detail={tx('ui.stock_requests.receive_adds', { count: request.itemCount.toLocaleString(), office: officeTitle(request.office) })}
        lines={request.lines} action={tx('ui.stock_requests.mark_received')} busyLabel={tx('ui.products.settings.saving')}
        onClose={() => setAsking(null)}
        onConfirm={async () => { await receiveStockRequest(request.uuid); setAsking(null); changed(); }} />
    )}
    {asking === 'cancel' && (
      <Confirm title={tx('ui.stock_requests.cancel_title', { number: request.number })} detail={tx('ui.stock_requests.cancel_detail')}
        withNote action={tx('ui.stock_requests.cancel')} busyLabel={tx('ui.products.settings.saving')}
        onClose={() => setAsking(null)}
        onConfirm={async (note) => { await cancelStockRequest(request.uuid, note); setAsking(null); changed(); }} />
    )}
  </>);
}

// What a line asks of a warehouse, and whether it has that many.
function lineStock(warehouse, line) {
  const product = warehouse.products.find((entry) => entry.productId === line.productId);
  if (!product) return 0;
  if (line.sku === null) return product.stock;
  return product.combinations.find((row) => row.sku === line.sku)?.stock ?? 0;
}

// A new request, in two steps: what is needed (a number per product, or per
// finish and size of a product with choices), then the warehouse it is asked
// of, the nearest first, each saying whether it has all of it. `preset`: the
// product it starts from, when it was opened from that product. `shop`: asked
// for a retail shop, which keeps its stock as a warehouse does (no store of its
// own: the catalogue as it is, and the shop's own stock).
export function NewStockRequest({ office, preset = null, shop = false, onClose, onSent }) {
  const [step, setStep] = useState('products');
  const catalogue = useLiveResource(['products'], useCallback(() => loadProducts(shop ? null : office.id), [office.id, shop]));
  const [query, setQuery] = useState('');
  // What is typed: a number per plain product, a box per combination of one with choices.
  const [amounts, setAmounts] = useState({});
  const [entries, setEntries] = useState({});
  const [stocks, setStocks] = useState({});
  const [opened, setOpened] = useState(preset?.productId ?? null);
  const [warehouses, setWarehouses] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // What the catalogue has now and sends in a parcel.
  const products = useMemo(() => (catalogue.data ?? []).filter((product) => keepsStock(product) && product.isActive)
    .sort((left, right) => left.name.localeCompare(right.name)), [catalogue.data]);
  const byId = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);

  // A product with choices: its finishes and sizes, as the office keeps them, read when it is opened.
  useEffect(() => {
    const product = opened === null ? null : byId.get(opened);
    if (!product || !product.stockPerCombination || stocks[opened]) return undefined;
    let live = true;
    (shop ? loadLocationProductStock(office.id, opened) : loadOfficeStock(office.id, opened))
      .then((answer) => { if (live) setStocks((had) => ({ ...had, [opened]: answer })); })
      .catch((caught) => { if (live) setError(errorMessage(caught)); });
    return () => { live = false; };
  }, [byId, office.id, opened, shop, stocks]);

  // Every line asked for, in the list's order.
  const lines = useMemo(() => products.flatMap((product) => {
    if (product.stockPerCombination) {
      const stock = stocks[product.id];
      const typed = entries[product.id] ?? {};
      return (stock?.combinations ?? []).filter((row) => countOf(typed[row.sku] ?? '') !== null)
        .map((row) => ({ productId: product.id, sku: row.sku, label: row.label, name: product.name, quantity: countOf(typed[row.sku]) }));
    }
    const count = countOf(amounts[product.id] ?? '');
    return count === null ? [] : [{ productId: product.id, sku: null, label: null, name: product.name, quantity: count }];
  }), [amounts, entries, products, stocks]);
  const unreadable = products.some((product) => (product.stockPerCombination
    ? Object.values(entries[product.id] ?? {}).some((typed) => !isLeft(typed) && countOf(typed) === null)
    : !isLeft(amounts[product.id]) && countOf(amounts[product.id] ?? '') === null));
  const inAll = lines.reduce((sum, line) => sum + line.quantity, 0);
  const touched = lines.length > 0 || note.trim() !== '';
  const close = useLeave(busy, touched, onClose);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle === '' ? products : products.filter((product) => `${product.name} ${product.category?.name ?? ''}`.toLowerCase().includes(needle));
  }, [products, query]);

  async function next() {
    if (unreadable) { setError(tx('ui.store.stock.error.quantity')); return; }
    if (lines.length === 0) { setError(tx('ui.store.stock.error.nothing')); return; }
    setError('');
    setBusy(true);
    try {
      const answer = await loadRequestWarehouses(office.id, [...new Set(lines.map((line) => line.productId))]);
      setWarehouses(answer.warehouses);
      // The nearest that has all of it is chosen.
      const first = answer.warehouses.find((warehouse) => lines.every((line) => lineStock(warehouse, line) >= line.quantity));
      setWarehouseId(first?.id ?? null);
      setStep('warehouse');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function send(event) {
    event.preventDefault();
    if (warehouseId === null) { setError(tx('ui.stock_requests.error.warehouse')); return; }
    setError('');
    setBusy(true);
    try {
      const made = await createStockRequest(office.id, {
        warehouseId,
        lines: lines.map((line) => (line.sku ? { productId: line.productId, sku: line.sku, quantity: line.quantity }
          : { productId: line.productId, quantity: line.quantity })),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      });
      onSent(made);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className="xo-pc-form is-request" role="dialog" aria-modal="true" aria-labelledby="xo-new-request-title"
        onSubmit={step === 'warehouse' ? send : (event) => { event.preventDefault(); next(); }}
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon"><PackagePlus aria-hidden="true" /></span>
          <div>
            <h2 id="xo-new-request-title">{tx('ui.stock_requests.new')}</h2>
            <OfficeNamed office={office} />
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>
        <ol className="xo-request-wizard" aria-label={tx('ui.stock_requests.new')}>
          <li className={step === 'products' ? 'is-now' : 'is-done'}><b>1</b>{tx('ui.stock_requests.step_products')}</li>
          <li className={step === 'warehouse' ? 'is-now' : undefined}><b>2</b>{tx('ui.stock_requests.step_warehouse')}</li>
        </ol>

        <div className="xo-pc-form-body">
          {step === 'products' && (catalogue.data === null ? <Loading /> : <>
            <SearchField value={query} onChange={setQuery} placeholder={tx("ui.products.search_products_4412bfe5")} />
            <div className="xo-request-pick">
              {shown.map((product) => {
                const picture = product.mainImageThumbnailUrl ?? product.imageUrl ?? null;
                const isOpen = opened === product.id;
                const asked = lines.filter((line) => line.productId === product.id).reduce((sum, line) => sum + line.quantity, 0);
                return (
                  <div key={product.id} className={`xo-request-pick-row${asked > 0 ? ' is-on' : ''}`}>
                    <div className="xo-request-pick-head">
                      <span className="xo-pc-thumb">{picture ? <img src={picture} alt="" loading="lazy" /> : <ImageIcon aria-hidden="true" />}</span>
                      <span className="xo-request-pick-name">
                        <strong>{product.name}</strong>
                        <small>{tx('ui.stock_requests.you_have', { count: (product.stock ?? 0).toLocaleString() })}</small>
                      </span>
                      {product.stockPerCombination ? (
                        <button type="button" className="xo-btn-2 xo-request-choose" aria-expanded={isOpen}
                          onClick={() => setOpened(isOpen ? null : product.id)}>
                          {asked > 0 ? tx('ui.stock_requests.chosen', { count: asked.toLocaleString() }) : tx('ui.shipments.choose_which')}
                          <ChevronDown aria-hidden="true" />
                        </button>
                      ) : (
                        <input type="text" inputMode="numeric" className="xo-request-amount" placeholder="0"
                          aria-label={tx('ui.stock_requests.how_many_of', { name: product.name })}
                          value={amounts[product.id] ?? ''} disabled={busy}
                          onChange={(event) => { setError(''); setAmounts((had) => ({ ...had, [product.id]: event.target.value })); }} />
                      )}
                    </div>
                    {product.stockPerCombination && isOpen && (stocks[product.id]
                      ? <EntryBoxes stock={stocks[product.id]} amounts={entries[product.id] ?? {}} disabled={busy}
                        onAmounts={(next) => { setError(''); setEntries((had) => ({ ...had, [product.id]: next })); }} />
                      : <Loading />)}
                  </div>
                );
              })}
              {shown.length === 0 && <Empty title={tx("ui.products.nothing_matches_17e7aebf")} />}
            </div>
          </>)}

          {step === 'warehouse' && (warehouses.length === 0 ? <Empty title={tx('ui.stock_requests.no_warehouses')} /> : <>
            <div className="xo-request-warehouses" role="radiogroup" aria-label={tx('ui.stock_requests.step_warehouse')}>
              {warehouses.map((warehouse) => {
                const short = lines.filter((line) => lineStock(warehouse, line) < line.quantity);
                const able = short.length === 0;
                const chosen = warehouseId === warehouse.id;
                return (
                  <label key={warehouse.id} className={`xo-request-warehouse${chosen ? ' is-on' : ''}${able ? '' : ' is-short'}`}>
                    <input type="radio" name="xo-request-warehouse" checked={chosen} disabled={!able || busy}
                      onChange={() => setWarehouseId(warehouse.id)} />
                    <span className="xo-request-warehouse-head">
                      <Warehouse aria-hidden="true" />
                      <span>
                        <strong>{warehouse.name}</strong>
                        <small>{placeAddress(warehouse)}</small>
                      </span>
                      {warehouse.distanceKm !== null && <b>{tx('ui.stock_requests.distance', { km: warehouse.distanceKm.toLocaleString() })}</b>}
                    </span>
                    <span className={`xo-request-warehouse-state${able ? ' is-able' : ''}`}>
                      {able ? tx('ui.stock_requests.has_all') : tx('ui.stock_requests.short_of')}
                    </span>
                    <ul>
                      {lines.map((line) => {
                        const has = lineStock(warehouse, line);
                        return (
                          <li key={`${line.productId}-${line.sku ?? ''}`} className={has < line.quantity ? 'is-short' : undefined}>
                            <span>{lineName(line)}</span>
                            <small>{tx('ui.stock_requests.has_of', { asked: line.quantity.toLocaleString(), has: has.toLocaleString() })}</small>
                          </li>
                        );
                      })}
                    </ul>
                  </label>
                );
              })}
            </div>
            <label className="xo-stock-field is-wide">
              <span>{tx('ui.store.stock.note')}</span>
              <input type="text" maxLength={500} value={note} disabled={busy} dir="auto" onChange={(event) => setNote(event.target.value)} />
            </label>
          </>)}
        </div>

        <footer className="xo-pc-form-foot">
          {error
            ? <p className="xo-alert t-bad" role="alert">{error}</p>
            : <span className="xo-stock-sum">{inAll > 0 ? tx('ui.store.stock.in_all', { count: inAll.toLocaleString() }) : ''}</span>}
          {step === 'warehouse'
            ? <button type="button" className="xo-btn-2" disabled={busy} onClick={() => { setError(''); setStep('products'); }}>
              <ArrowLeft aria-hidden="true" />{tx('ui.stock_requests.back')}</button>
            : <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>{tx('ui.common.cancel')}</button>}
          <button type="submit" className="xo-btn" disabled={busy || (step === 'warehouse' && warehouseId === null)}>
            {step === 'products'
              ? (busy ? tx('ui.store.stock.reading') : tx('ui.stock_requests.next'))
              : (busy ? tx('ui.products.settings.saving') : tx('ui.stock_requests.send'))}
          </button>
        </footer>
      </form>
    </div>
  );
}

// The warehouse (or retail shop) whose requests the page is: the account's
// own, or the one chosen (?location=) by an account that keeps every one's stock.
function usePlace(kind) {
  const places = useLiveResource(['locations'], useCallback(() => loadStockLocations(), []));
  const [searchParams] = useSearchParams();
  const ofKind = useMemo(() => (places.data?.locations ?? []).filter((place) => place.kind === kind), [kind, places.data]);
  const wanted = Number(searchParams.get('location'));
  const place = ofKind.find((entry) => entry.id === wanted)
    ?? ofKind.find((entry) => entry.id === places.data?.ownId) ?? ofKind[0] ?? null;
  return { places, ofKind, place };
}

function useWarehouse() {
  const { places, ofKind, place } = usePlace('warehouse');
  return { places, warehouses: ofKind, warehouse: place };
}

function WarehouseLine({ warehouse, warehouses, onChoose, Icon = Warehouse }) {
  return (
    <div className="xo-whose">
      <Icon aria-hidden="true" />
      {warehouses.length > 1 ? (
        <XomicsSelect className="xo-whose-choice" aria-label={tx('ui.location_stock.of')} value={String(warehouse.id)}
          onChange={(event) => onChoose(Number(event.target.value))}>
          {warehouses.map((entry) => <option key={entry.id} value={String(entry.id)}>{entry.name}</option>)}
        </XomicsSelect>
      ) : <strong>{warehouse.name}</strong>}
      <address className="xo-whose-address">{placeAddress(warehouse)}</address>
    </div>
  );
}

function RequestsBody({
  side, requests, loading, pictures, notice, onNotice, onChanged,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = TABS[side];
  const counted = (entry) => requests.filter((request) => entry.statuses.includes(request.status)).length;
  // The page opens on the first tab that has something in it.
  const [tab, setTab] = useState(() => (tabs.find((entry) => counted(entry) > 0) ?? tabs[0]).value);
  const [query, setQuery] = useState('');
  const openUuid = searchParams.get('request');
  const chosen = tabs.find((entry) => entry.value === tab) ?? tabs[0];
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return requests.filter((request) => chosen.statuses.includes(request.status))
      .filter((request) => needle === '' || [String(request.number), request.office.name, request.warehouse.name, request.note ?? '']
        .some((value) => value.toLowerCase().includes(needle)));
  }, [chosen, query, requests]);
  const open = (uuid) => {
    const next = new URLSearchParams(searchParams);
    if (uuid === null) next.delete('request'); else next.set('request', uuid);
    setSearchParams(next, { replace: true });
  };
  return (<>
    {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={query} onChange={setQuery} placeholder={tx('ui.stock_requests.search')} />
      </div>
      <div className="xo-product-section-row">
        <WorkspaceTabs className="xo-product-sections" value={chosen.value} onChange={setTab} label={tx('ui.store.status')}
          tabs={tabs.map((entry) => ({ value: entry.value, label: tx(entry.label), count: counted(entry) }))} />
      </div>
    </div>
    <div className={loading ? 'xo-store-turning' : undefined}>
      <RequestList requests={shown} side={side} onOpen={(uuid) => { onNotice(''); open(uuid); }} />
    </div>
    {openUuid && <RequestSheet key={openUuid} uuid={openUuid} pictures={pictures} onClose={() => open(null)} onChanged={onChanged} />}
  </>);
}

// The pictures of the catalogue's products, by product, for the lines of a request.
function usePictures() {
  const catalogue = useLiveResource(['products'], useCallback(() => loadProducts(), []));
  return useMemo(() => new Map((catalogue.data ?? []).map((product) => [product.id, product.mainImageThumbnailUrl ?? product.imageUrl ?? null])),
    [catalogue.data]);
}

function OfficeRequests() {
  const { permissions } = useXomicsAuth();
  const store = useStoreOffice();
  const officeId = store.office?.id ?? null;
  const list = useLiveResource(['stock_requests'], useCallback(() => loadOfficeStockRequests(officeId), [officeId]), { enabled: officeId !== null });
  const pictures = usePictures();
  const [asking, setAsking] = useState(false);
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const mayAsk = permissions.includes('STOCK_REQUEST_MANAGE');
  const head = (
    <Head title={tx('ui.nav.stock_requests')} under={<StoreOfficeLine store={store} />}>
      {mayAsk && officeId !== null && (
        <button type="button" className="xo-btn" onClick={() => { setNotice(''); setAsking(true); }}>
          <PackagePlus aria-hidden="true" />{tx('ui.stock_requests.new')}
        </button>
      )}
    </Head>
  );
  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading) return <Loading />;
  if (officeId === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.store.no_office')} /></div>;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  if (list.data === null) return <Loading />;
  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      <RequestsBody side="office" requests={list.data.requests} loading={list.loading} pictures={pictures}
        notice={notice} onNotice={setNotice} onChanged={() => list.refresh()} />
      {asking && <NewStockRequest office={store.office} onClose={() => setAsking(false)}
        onSent={(made) => {
          setAsking(false);
          setNotice(tx('ui.stock_requests.sent', { number: made.number, warehouse: made.warehouse.name }));
          list.refresh();
          const next = new URLSearchParams(searchParams);
          next.set('request', made.uuid);
          setSearchParams(next, { replace: true });
        }} />}
    </div>
  );
}

function WarehouseRequests() {
  const { places, warehouses, warehouse } = useWarehouse();
  const warehouseId = warehouse?.id ?? null;
  const list = useLiveResource(['stock_requests'], useCallback(() => loadWarehouseStockRequests(warehouseId), [warehouseId]),
    { enabled: warehouseId !== null });
  const pictures = usePictures();
  const [notice, setNotice] = useState('');
  const [, setSearchParams] = useSearchParams();
  const head = warehouse === null
    ? <Head title={tx('ui.nav.warehouse_requests')} subtitle={tx('ui.stock_requests.subtitle_warehouse')} />
    : <Head title={tx('ui.nav.warehouse_requests')} under={<WarehouseLine warehouse={warehouse} warehouses={warehouses}
      onChoose={(id) => setSearchParams({ location: String(id) }, { replace: true })} />} />;
  if (places.error) return <Failed message={places.error} onRetry={places.refresh} />;
  if (places.data === null) return <Loading />;
  if (warehouse === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.location_stock.no_warehouse')} /></div>;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  if (list.data === null) return <Loading />;
  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      <RequestsBody side="warehouse" requests={list.data.requests} loading={list.loading} pictures={pictures}
        notice={notice} onNotice={setNotice} onChanged={() => list.refresh()} />
    </div>
  );
}

// A retail shop's side, as an office's: what it asked the warehouses for, a new
// request, and taking it in once it arrives.
function ShopRequests() {
  const { permissions } = useXomicsAuth();
  const { places, ofKind, place } = usePlace('retail');
  const placeId = place?.id ?? null;
  const list = useLiveResource(['stock_requests'], useCallback(() => loadOfficeStockRequests(placeId), [placeId]), { enabled: placeId !== null });
  const pictures = usePictures();
  const [asking, setAsking] = useState(false);
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const mayAsk = permissions.includes('STOCK_REQUEST_MANAGE');
  const head = place === null
    ? <Head title={tx('ui.nav.stock_requests')} />
    : <Head title={tx('ui.nav.stock_requests')} under={<WarehouseLine warehouse={place} warehouses={ofKind} Icon={ShoppingBag}
      onChoose={(id) => setSearchParams({ location: String(id) }, { replace: true })} />}>
      {mayAsk && (
        <button type="button" className="xo-btn" onClick={() => { setNotice(''); setAsking(true); }}>
          <PackagePlus aria-hidden="true" />{tx('ui.stock_requests.new')}
        </button>
      )}
    </Head>;
  if (places.error) return <Failed message={places.error} onRetry={places.refresh} />;
  if (places.data === null) return <Loading />;
  if (place === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.store.no_office')} /></div>;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  if (list.data === null) return <Loading />;
  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      <RequestsBody side="office" requests={list.data.requests} loading={list.loading} pictures={pictures}
        notice={notice} onNotice={setNotice} onChanged={() => list.refresh()} />
      {asking && <NewStockRequest office={place} shop onClose={() => setAsking(false)}
        onSent={(made) => {
          setAsking(false);
          setNotice(tx('ui.stock_requests.sent', { number: made.number, warehouse: made.warehouse.name }));
          list.refresh();
          const next = new URLSearchParams(searchParams);
          next.set('request', made.uuid);
          setSearchParams(next, { replace: true });
        }} />}
    </div>
  );
}

// `side`: 'office' (Selling, Stock requests), 'warehouse' (Warehouse, Requests)
// or 'retail' (Retail, Stock requests).
export default function XomicsStockRequests({ side }) {
  if (side === 'warehouse') return <WarehouseRequests />;
  return side === 'retail' ? <ShopRequests /> : <OfficeRequests />;
}
